import type { CartData } from "../interfaces/CartData";
import type { CartItem } from "../interfaces/CartItem";
import type { CartCategoryId } from "../interfaces/CartCategoryId";

/**
 * When any listed category id appears under `cartItems.cartItem[].product.categories`
 * from `GET /cart/active`, the given US state codes apply to **shipping** only: omitted
 * from the shipping state dropdown in Places and blocked for restricted autocomplete
 * selections. Billing is not restricted. Review order (Verify) blocks submit only if
 * **shipping** is US and uses a disallowed subdivision.
 *
 * @example
 * { categoryIds: [288, 289], usStateCodesToRemoveFromDropdown: ["AK", "HI"] }
 */
export type ProductUsStateRestrictionRule = {
  categoryIds: (number | string)[];
  usStateCodesToRemoveFromDropdown: string[];
};

export const PRODUCT_US_STATE_RESTRICTIONS: ProductUsStateRestrictionRule[] = [
  { categoryIds: [288, 289, 25, 40], usStateCodesToRemoveFromDropdown: ["CA"] },
];

function normalizeCategoryId(entry: CartCategoryId | undefined | null): string | null {
  if (entry === undefined || entry === null) {
    return null;
  }
  if (typeof entry === "number") {
    return String(entry);
  }
  if (typeof entry === "string") {
    const t = entry.trim();
    return t.length ? t : null;
  }
  if (typeof entry === "object" && entry !== null && "id" in entry) {
    const id = (entry as { id?: number | string }).id;
    if (id === undefined || id === null) {
      return null;
    }
    const s = String(id).trim();
    return s.length ? s : null;
  }
  return null;
}

function addCategoriesFromList(target: Set<string>, list: CartCategoryId[] | undefined | null): void {
  if (!list?.length) {
    return;
  }
  for (const c of list) {
    const n = normalizeCategoryId(c);
    if (n) {
      target.add(n);
    }
  }
}

/** Union of normalized category ids from each line's `product.categories` (`/cart/active`). */
function collectCartCategoryIds(cartItems: CartItem[] | undefined): Set<string> {
  const ids = new Set<string>();
  if (!cartItems?.length) {
    return ids;
  }
  for (const item of cartItems) {
    addCategoriesFromList(ids, item.product?.categories);
  }
  return ids;
}

function normalizeUsStateCode(code: string): string {
  return code.trim().toUpperCase();
}

/** Union of state codes to hide on shipping (Places) when any rule's category ids match the cart. */
export function getUsStateCodesExcludedForCart(cartItems: CartItem[] | undefined): string[] {
  if (!PRODUCT_US_STATE_RESTRICTIONS.length) {
    return [];
  }
  const cartCategoryIds = collectCartCategoryIds(cartItems);
  if (!cartCategoryIds.size) {
    return [];
  }
  const excluded = new Set<string>();
  for (const rule of PRODUCT_US_STATE_RESTRICTIONS) {
    const match = rule.categoryIds.some((id) => cartCategoryIds.has(String(id)));
    if (match) {
      for (const code of rule.usStateCodesToRemoveFromDropdown) {
        if (code?.trim()) {
          excluded.add(normalizeUsStateCode(code));
        }
      }
    }
  }
  return [...excluded];
}

export function isUsAddressUsingExcludedSubdivision(
  country: string | undefined,
  countrySubdivision: string | undefined,
  excludedCodes: readonly string[]
): boolean {
  if (!excludedCodes.length || (country || "").toUpperCase() !== "US") {
    return false;
  }
  const sub = (countrySubdivision || "").trim();
  if (!sub) {
    return false;
  }
  const set = new Set(excludedCodes.map(normalizeUsStateCode));
  return set.has(normalizeUsStateCode(sub));
}

/** True when cart categories trigger rules and shipping US subdivision is disallowed (billing is not checked). */
export function cartViolatesProductUsStateRules(cartData: CartData | undefined): boolean {
  if (!cartData) {
    return false;
  }
  const ex = getUsStateCodesExcludedForCart(cartData.cartItems?.cartItem);
  if (!ex.length) {
    return false;
  }
  return isUsAddressUsingExcludedSubdivision(
    cartData.shippingAddress?.country,
    cartData.shippingAddress?.countrySubdivision,
    ex
  );
}
